import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import Icon from '../../../components/AppIcon';

const ValuesSection = () => {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, threshold: 0.3 });

  const coreValues = [
    {
      icon: "Sparkles",
      title: "Innovation Excellence",
      description: `We integrate the timeless principles of Ayurveda with modern clinical understanding to deliver structured, effective treatment protocols. \nOur commitment to continuous improvement ensures every patient receives the most refined and reliable care.`,
      color: "from-primary to-accent"
    },
    {
      icon: "Heart",
      title: "Client-Centered Care",
      description: `Your wellbeing is at the heart of every decision.
      We don’t believe in isolating conditions into organ-based specializations. \nOur holistic Ayurvedic care restores harmony throughout the entire body for meaningful, sustained healing.`,
      color: "from-accent to-primary"
    },
    {
      icon: "Award",
      title: "Uncompromising Quality",
      description: `We maintain uncompromising quality across all aspects of care—from herbal formulations and therapeutic procedures to patient guidance and follow-ups. \nOur dedication ensures each individual receives authentic, reliable, and effective Ayurvedic treatment.`,
      color: "from-primary to-secondary"
    },
    {
      icon: "Users",
      title: "Inclusive Health",
      description: `Ayurveda teaches that well-being is universal and deeply personal. \nWe honour diversity by providing compassionate, holistic care that strengthens immunity, restores balance, and supports long-term wellness—regardless of background or health history.`,
      color: "from-secondary to-accent"
    },
    {
      icon: "Leaf",
      title: "Sustainable Practices",
      description: `We are committed to environmental responsibility through eco-friendly products, mindful resource use, and sustainable clinical practices.\nOur holistic health center operates with deep respect for our patients, our community, and the wellbeing of our planet.`,
      color: "from-accent to-primary"
    },
    {
      icon: "Lightbulb",
      title: "Continuous Learning",
      description: `We invest in ongoing education and skill advancement to stay at the forefront of holistic healthcare and immune science.\nOur team continuously updates clinical knowledge—combining modern research with Ayurvedic wisdom—to provide safer, more effective, and evidence-aligned care for every patient.`,
      color: "from-primary to-accent"
    }
  ];

  const achievements = [
    {
      icon: "Trophy",
      title: "Industry Recognition",
      enabled: false,
      stats: [
        "Best Beauty Salon 2023",
        "Innovation Award Winner",
        "Client Choice Award",
        "Sustainability Leader"
      ]
    },
    {
      icon: "Users",
      title: "Client Success",
      enabled: true,
      stats: [
        "98% Satisfaction Rate",
        "5000+ Transformations",
        "95% Return Clients",
        "5/5 Average Rating"
      ]
    },
    {
      icon: "Zap",
      title: "Technology Leadership",
      enabled: false,
      stats: [
        "First AR Try-On System",
        "3D Consultation Pioneer",
        "AI Skin Analysis",
        "Digital Beauty Platform"
      ]
    }
  ];

  return (
    <section ref={sectionRef} className="py-20 lg:py-32 bg-gradient-to-b from-background to-muted/30">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-20"
        >
          <h2 className="font-headline text-3xl md:text-5xl font-bold text-foreground mb-6">
            Our Core <span className="text-morph">Values</span>
          </h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto font-accent">
            These fundamental principles guide every aspect of our work and define the 
            exceptional experience we create for each client who enters our sanctuary.
          </p>
        </motion.div>

        {/* Values Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-20">
          {coreValues?.map((value, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 50 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: index * 0.1 }}
              className="group"
            >
              <div className="glass-card p-8 h-full morph-hover relative overflow-hidden">
                {/* Background Gradient */}
                <div className={`absolute inset-0 bg-gradient-to-br ${value?.color} opacity-0 group-hover:opacity-5 transition-opacity duration-500`}></div>
                
                <div className="relative z-10">
                  {/* Icon */}
                  <div className="mb-6">
                    <div className={`w-16 h-16 rounded-full bg-gradient-to-br ${value?.color} p-0.5 mx-auto`}>
                      <div className="w-full h-full bg-background rounded-full flex items-center justify-center">
                        <Icon name={value?.icon} size={28} className="text-primary" />
                      </div>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="text-center">
                    <h3 className="text-xl font-bold text-foreground mb-4 font-headline">
                      {value?.title}
                    </h3>
                    <p className="text-muted-foreground leading-relaxed whitespace-pre-line">
                      {value?.description}
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Achievements Section */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="flex flex-wrap justify-center gap-8"
        >
          {achievements?.filter(achievement => achievement.enabled != false).map((achievement, index) => (
            <div key={index} className="glass-card p-8 text-center morph-hover">
              <div className="mb-6">
                <div className="w-20 h-20 rounded-full bg-gradient-to-br from-primary to-accent p-0.5 mx-auto">
                  <div className="w-full h-full bg-background rounded-full flex items-center justify-center">
                    <Icon name={achievement?.icon} size={32} className="text-primary" />
                  </div>
                </div>
              </div>
              
              <h3 className="text-xl font-bold text-foreground mb-6 font-headline">
                {achievement?.title}
              </h3>
              
              <div className="space-y-3">
                {achievement?.stats?.map((stat, idx) => (
                  <div key={idx} className="flex items-center justify-center gap-3">
                    <Icon name="CheckCircle" size={16} className="text-primary flex-shrink-0" />
                    <span className="text-muted-foreground font-medium">{stat}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </motion.div>

        {/* Mission Statement */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.8 }}
          className="mt-20"
        >
          <div className="glass-card p-12 text-center max-w-4xl mx-auto relative overflow-hidden">
            {/* Background Pattern */}
            <div className="absolute inset-0 opacity-5">
              <div className="absolute top-10 left-10 w-32 h-32 rounded-full bg-gradient-to-br from-primary to-accent float-gentle"></div>
              <div className="absolute bottom-10 right-10 w-24 h-24 rounded-full bg-gradient-to-br from-accent to-primary float-bubble"></div>
            </div>
            
            <div className="relative z-10">
              <Icon name="Target" size={48} className="text-primary mx-auto mb-8" />
              <h3 className="text-2xl md:text-3xl font-bold text-foreground mb-6 font-headline">
                Our Mission
              </h3>
              <p className="text-lg md:text-xl text-muted-foreground leading-relaxed font-accent">
                To transform healthcare by providing evidence-aligned, holistic, and patient-centered care that strengthens immunity, restores balance, and supports long-term well-being.
                We integrate advanced diagnostic insights, traditional Ayurvedic wisdom, and compassionate clinical practice to help every individual achieve their healthiest, most resilient self—naturally and sustainably.
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ValuesSection;